import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, TextField } from '@mui/material';
import { caisseAPI, transactionAPI } from '../../services/api';
import rapprochementAPI from '../../services/rapprochementAPI';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import FilterInput from '../../components/ui/FilterInput';
import DateRangeFilter from '../../components/ui/DateRangeFilter';
import ActionButton from '../../components/ui/ActionButton';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import arrowBackIcon from '../../assets/icons/arrow-back.svg';

const HEADER_HEIGHT = 76;
const SIDEBAR_WIDTH = '340px';

const ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  background: '#FFFFFF',
  borderRadius: '12px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  padding: '10px 12px',
  marginBottom: '6px',
  gap: '10px',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background 0.15s',
};

const SIDEBAR_ROW = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '9px 0',
};

const SIDEBAR_LABEL = {
  fontFamily: 'Montserrat',
  fontSize: '12px',
  fontWeight: 500,
  color: '#000000',
  opacity: 0.88,
};

const SIDEBAR_VALUE = {
  fontFamily: 'Montserrat',
  fontSize: '13px',
  fontWeight: 600,
  color: '#38485C',
};

const Checkbox = ({ checked, onChange, indeterminate }) => (
  <div style={{ position: 'relative', flexShrink: 0, width: '16px', height: '16px' }}>
    <input
      type="checkbox"
      checked={checked}
      ref={el => { if (el) el.indeterminate = !!indeterminate; }}
      onChange={onChange}
      onClick={e => e.stopPropagation()}
      style={{
        width: '16px', height: '16px',
        border: '1px solid #C5C5C5', borderRadius: '4px',
        appearance: 'none', WebkitAppearance: 'none',
        backgroundColor: checked || indeterminate ? '#007BC6' : 'white',
        cursor: 'pointer', position: 'relative',
      }}
    />
    {(checked || indeterminate) && (
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
        width: indeterminate ? '8px' : '10px', height: indeterminate ? '2px' : '10px',
        backgroundColor: indeterminate ? 'white' : 'transparent',
        borderRadius: '1px', pointerEvents: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && !indeterminate && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    )}
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear()} - ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
};

const getSignedAmount = (t) => (Number(t.amount) || 0) * (Number(t.caisseFactor) || 1);

const formatSigned = (val) => {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  return formatAmountForDisplay(n, n < 0).text;
};

const ecartColor = (val) => {
  if (val === null || val === undefined) return '#38485C';
  return val > 0 ? '#0AD69F' : val < 0 ? '#EF456E' : '#919191';
};

const CORRECTION_LABELS = ['Autres augmente', 'Autres diminue'];

const NouveauRapprochementStep2 = () => {
  const { versementId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [versement, setVersement] = useState(location.state?.versement || null);
  const [loadingVersement, setLoadingVersement] = useState(!location.state?.versement);
  const [commercialTxs, setCommercialTxs] = useState([]);
  const [verificationTxs, setVerificationTxs] = useState([]);
  const [loadingCommercial, setLoadingCommercial] = useState(false);
  const [loadingVerification, setLoadingVerification] = useState(false);
  const [error, setError] = useState(null);

  const [selectedCommercialIds, setSelectedCommercialIds] = useState([]);
  const [selectedVerificationIds, setSelectedVerificationIds] = useState([]);

  const [commercialSearch, setCommercialSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [verificationSearch, setVerificationSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDirection, setAdjustmentDirection] = useState(null);
  const [adjustmentNote, setAdjustmentNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const DEFAULT = 'Ajustement - Rapprochement';
    if (adjustmentAmount && !adjustmentNote) {
      setAdjustmentNote(DEFAULT);
    } else if (!adjustmentAmount && adjustmentNote === DEFAULT) {
      setAdjustmentNote('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustmentAmount]);

  const [commercialSortBy, setCommercialSortBy] = useState('createdAt');
  const [commercialSortDir, setCommercialSortDir] = useState('DESC');
  const [verificationSortBy, setVerificationSortBy] = useState('createdAt');
  const [verificationSortDir, setVerificationSortDir] = useState('DESC');

  const handleCommercialSort = (field) => {
    setCommercialSortDir(commercialSortBy === field && commercialSortDir === 'ASC' ? 'DESC' : 'ASC');
    setCommercialSortBy(field);
  };
  const handleVerificationSort = (field) => {
    setVerificationSortDir(verificationSortBy === field && verificationSortDir === 'ASC' ? 'DESC' : 'ASC');
    setVerificationSortBy(field);
  };

  useEffect(() => {
    if (versement) return;
    transactionAPI.getVersementsNonVerifies({})
      .then(res => {
        const found = (res.data || []).find(v => String(v.id) === String(versementId));
        if (found) setVersement(found);
        else setError('Versement introuvable');
      })
      .catch(() => setError('Échec du chargement du versement'))
      .finally(() => setLoadingVersement(false));
  }, [versementId, versement]);

  useEffect(() => {
    if (!versement) return;
    const caisseId = versement.caisse?.id || versement.caisseId;
    const caisseName = versement.caisse?.name || versement.caisseName || '';

    setLoadingCommercial(true);
    transactionAPI.getRapprochementCandidates({ caisseId })
      .then(res => setCommercialTxs(res.data || []))
      .catch(() => setError('Échec du chargement des transactions'))
      .finally(() => setLoadingCommercial(false));

    setLoadingVerification(true);
    caisseAPI.getForFilters(false)
      .then(res => {
        const verifCaisse = (res.data || []).find(c => c.name === 'Vérification');
        if (!verifCaisse) throw new Error('Caisse Vérification introuvable');
        return transactionAPI.getVerificationsCandidates({ caisseId: verifCaisse.id });
      })
      .then(res => {
        const all = res.data || [];
        const named = all.filter(t => t.category?.label === caisseName);
        const corrections = all.filter(t => CORRECTION_LABELS.includes(t.category?.label));
        const namedBcRefs = new Set(
          named.flatMap(t => (t.references || []).filter(r => r.type === 'BC').map(r => r.value))
        );
        const relevantCorrections = namedBcRefs.size > 0
          ? corrections.filter(t => (t.references || []).some(r => r.type === 'BC' && namedBcRefs.has(r.value)))
          : [];
        setVerificationTxs([...named, ...relevantCorrections]);
      })
      .catch(err => setError(err.message || 'Échec du chargement des vérifications'))
      .finally(() => setLoadingVerification(false));
  }, [versement]);

  const availableCategories = useMemo(() =>
    [...new Set(commercialTxs.map(t => t.category?.label).filter(Boolean))].sort()
  , [commercialTxs]);

  const filteredCommercialTxs = useMemo(() => {
    const filtered = commercialTxs.filter(t => {
      if (selectedCategories.length > 0 && !selectedCategories.includes(t.category?.label)) return false;
      if (dateRange?.start || dateRange?.end) {
        const txDate = new Date(t.createdAt);
        if (dateRange.start) { const s = new Date(dateRange.start); s.setHours(0,0,0,0); if (txDate < s) return false; }
        if (dateRange.end)   { const e = new Date(dateRange.end);   e.setHours(23,59,59,999); if (txDate > e) return false; }
      }
      if (!commercialSearch) return true;
      const term = commercialSearch.toLowerCase();
      const nt = term.replace(/[\s,]/g, '');
      const bc = (t.references || []).find(r => r.type === 'BC')?.value || '';
      return (
        (t.code || '').toLowerCase().includes(term) ||
        (t.description || '').toLowerCase().includes(term) ||
        bc.toLowerCase().includes(term) ||
        String(t.amount ?? '').includes(nt)
      );
    });
    return [...filtered].sort((a, b) => {
      const dir = commercialSortDir === 'ASC' ? 1 : -1;
      if (commercialSortBy === 'createdAt') return dir * (new Date(a.createdAt) - new Date(b.createdAt));
      if (commercialSortBy === 'category') return dir * (a.category?.label || '').localeCompare(b.category?.label || '');
      if (commercialSortBy === 'amount') return dir * (getSignedAmount(a) - getSignedAmount(b));
      return 0;
    });
  }, [commercialTxs, commercialSearch, selectedCategories, commercialSortBy, commercialSortDir, dateRange]);

  const filteredVerificationTxs = useMemo(() => {
    const filtered = verificationTxs.filter(t => {
      if (dateRange?.start || dateRange?.end) {
        const txDate = new Date(t.createdAt);
        if (dateRange.start) { const s = new Date(dateRange.start); s.setHours(0,0,0,0); if (txDate < s) return false; }
        if (dateRange.end)   { const e = new Date(dateRange.end);   e.setHours(23,59,59,999); if (txDate > e) return false; }
      }
      if (!verificationSearch) return true;
      const term = verificationSearch.toLowerCase();
      const nt = term.replace(/[\s,]/g, '');
      const bc = (t.references || []).find(r => r.type === 'BC')?.value || '';
      return (
        (t.code || '').toLowerCase().includes(term) ||
        (t.description || '').toLowerCase().includes(term) ||
        bc.toLowerCase().includes(term) ||
        String(t.amount ?? '').includes(nt)
      );
    });
    return [...filtered].sort((a, b) => {
      const dir = verificationSortDir === 'ASC' ? 1 : -1;
      if (verificationSortBy === 'createdAt') return dir * (new Date(a.createdAt) - new Date(b.createdAt));
      if (verificationSortBy === 'bc') {
        const bcA = (a.references || []).find(r => r.type === 'BC')?.value || '';
        const bcB = (b.references || []).find(r => r.type === 'BC')?.value || '';
        return dir * bcA.localeCompare(bcB);
      }
      if (verificationSortBy === 'amount') return dir * (a.amount - b.amount);
      return 0;
    });
  }, [verificationTxs, verificationSearch, verificationSortBy, verificationSortDir, dateRange]);

  // Signed net (sales - frais) for selected commercial transactions
  const transactionsNet = useMemo(() =>
    commercialTxs.filter(t => selectedCommercialIds.includes(t.id))
      .reduce((s, t) => s + getSignedAmount(t), 0)
  , [commercialTxs, selectedCommercialIds]);

  // Gross sales only (caisseFactor >= 1) for selected commercial transactions
  const selectedSalesTxs = useMemo(() =>
    commercialTxs.filter(t =>
      selectedCommercialIds.includes(t.id) &&
      Number(t.caisseFactor) >= 1 &&
      (t.references || []).some(r => r.type === 'BC')
    )
  , [commercialTxs, selectedCommercialIds]);

  const transactionsSales = useMemo(() =>
    selectedSalesTxs.reduce((s, t) => s + (Number(t.amount) || 0), 0)
  , [selectedSalesTxs]);

  const verificationNet = useMemo(() =>
    verificationTxs.filter(t => selectedVerificationIds.includes(t.id))
      .reduce((s, t) => s + (Number(t.amount) || 0) * (Number(t.caisseFactor) || 1), 0)
  , [verificationTxs, selectedVerificationIds]);

  const versementAmount = Number(versement?.amount) || 0;
  const hasCommercialSelection = selectedCommercialIds.length > 0;
  const hasVerificationSelection = selectedVerificationIds.length > 0;

  const ecartVT = hasCommercialSelection ? versementAmount - transactionsNet : null;
  const ecartTV = hasCommercialSelection && hasVerificationSelection ? transactionsSales - verificationNet : null;

  useEffect(() => {
    if (ecartVT === null && ecartTV === null) return;
    const total = parseFloat(((ecartVT ?? 0) + (ecartTV ?? 0)).toFixed(2));
    if (total > 0) {
      setAdjustmentAmount(String(total));
      setAdjustmentDirection('DEDUCT');
    } else if (total < 0) {
      setAdjustmentAmount(String(Math.abs(total)));
      setAdjustmentDirection('ADD');
    } else {
      setAdjustmentAmount('');
      setAdjustmentDirection(null);
    }
  }, [ecartVT, ecartTV]);

  const isAllCommercialSelected = filteredCommercialTxs.length > 0 && filteredCommercialTxs.every(t => selectedCommercialIds.includes(t.id));
  const isSomeCommercialSelected = filteredCommercialTxs.some(t => selectedCommercialIds.includes(t.id)) && !isAllCommercialSelected;
  const isAllVerificationSelected = filteredVerificationTxs.length > 0 && filteredVerificationTxs.every(t => selectedVerificationIds.includes(t.id));
  const isSomeVerificationSelected = filteredVerificationTxs.some(t => selectedVerificationIds.includes(t.id)) && !isAllVerificationSelected;

  const handleSelectAllCommercial = (e) => {
    setSelectedCommercialIds(e.target.checked ? filteredCommercialTxs.map(t => t.id) : []);
  };
  const handleSelectAllVerification = (e) => {
    setSelectedVerificationIds(e.target.checked ? filteredVerificationTxs.map(t => t.id) : []);
  };
  const toggleCommercial = (id) => {
    setSelectedCommercialIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleVerification = (id) => {
    setSelectedVerificationIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleConfirmClick = () => setConfirmOpen(true);

  const handleConfirmSubmit = async () => {
    setSubmitLoading(true);
    setError(null);
    try {
      const res = await rapprochementAPI.create({
        versementId: Number(versementId),
        commercialTransactionIds: selectedCommercialIds,
        verificationTransactionIds: selectedVerificationIds,
        adjustmentAmount: adjustmentAmount ? Number(adjustmentAmount) : null,
        adjustmentDirection: adjustmentAmount ? adjustmentDirection : null,
        adjustmentNote: adjustmentNote || null,
      });
      setConfirmOpen(false);
      history.push(`/rapprochements/${res.data.id}`);
    } catch (err) {
      setConfirmOpen(false);
      setError(err.response?.data?.message || 'Échec de la création du rapprochement');
    } finally {
      setSubmitLoading(false);
    }
  };

  const categoryOptions = availableCategories.map(c => ({ value: c, label: c }));

  if (loadingVersement) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  const caisseName = versement?.caisse?.name || versement?.caisseName || '';
  const versementCode = versement?.code || '';
  const versementDate = versement?.createdAt ? formatDate(versement.createdAt) : '';
  const soldeLorsVersement = versement?.caisseAfter ?? versement?.coffreBefore ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${HEADER_HEIGHT}px)`, overflow: 'hidden', background: '#F8F8F8' }}>

      {/* Sticky page header */}
      <Box sx={{ flexShrink: 0, background: 'linear-gradient(to bottom, #fff, #F8F8F8)', px: { xs: '26px', sm: '42px' }, pt: '24px', pb: '16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <p style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '14px', color: '#919191', margin: '0 0 4px 0' }}>
            Nouveau rapprochement
          </p>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '28px', lineHeight: '32px', color: '#38485C', opacity: 0.88, margin: 0 }}>
            <span style={{ color: '#027BC6' }}>Étape 2 :</span> Sélection des transactions et de la vérification
          </h1>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
          <DateRangeFilter dateRange={dateRange} onChange={setDateRange} placeholder="Toutes" />
        </Box>
      </Box>

      {/* Main 3-column layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', gap: '16px', px: { xs: '26px', sm: '42px' }, pb: '16px' }}>

        {/* LEFT PANEL — Commercial transactions */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* Panel header */}
          <Box sx={{ flexShrink: 0, pb: '12px' }}>
            <p style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px', color: '#38485C', margin: '0 0 12px 0' }}>
              Liste des transactions
            </p>
            <Box sx={{ display: 'flex', gap: '10px' }}>
              <FilterInput
                type="search"
                value={commercialSearch}
                onChange={setCommercialSearch}
                onClear={() => setCommercialSearch('')}
                hasValue={!!commercialSearch}
              />
              <Box sx={{ width: '200px', flexShrink: 0 }}>
                <FilterInput
                  type="dropdown"
                  label="Catégorie"
                  name="selectedCategories"
                  options={categoryOptions}
                  value={selectedCategories}
                  onChange={setSelectedCategories}
                  onClear={() => setSelectedCategories([])}
                  isMultiSelect={true}
                  hasValue={selectedCategories.length > 0}
                  placeholder="Toutes"
                />
              </Box>
            </Box>
          </Box>

          {/* Column headers */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: '12px', pb: '6px', gap: '10px' }}>
            <Checkbox checked={isAllCommercialSelected} indeterminate={isSomeCommercialSelected} onChange={handleSelectAllCommercial} />
            {[
              { key: 'createdAt', label: 'Transaction ID', sx: { flex: 1 } },
              { key: 'category',  label: 'Catégorie / N° BC', sx: { flex: 1 } },
              { key: 'amount',    label: 'Montant', sx: { width: '130px', flexShrink: 0 } },
            ].map(col => (
              <Box key={col.key} onClick={() => handleCommercialSort(col.key)} sx={{
                ...col.sx, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600,
                color: commercialSortBy === col.key ? '#027BC6' : '#919191',
                '&:hover': { color: '#027BC6' }, transition: 'color 0.2s',
              }}>
                <span>{col.label}</span>
                {commercialSortBy === col.key && (
                  <img src={chevronUpIcon} alt="" style={{ width: '12px', height: '12px', transform: commercialSortDir === 'DESC' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Scrollable rows */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loadingCommercial ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={28} /></Box>
            ) : filteredCommercialTxs.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 4, fontFamily: 'Montserrat', fontSize: '14px', color: '#919191' }}>
                Aucune transaction disponible
              </Box>
            ) : filteredCommercialTxs.map(tx => {
              const selected = selectedCommercialIds.includes(tx.id);
              const signed = getSignedAmount(tx);
              const amountColor = signed > 0 ? '#0AD69F' : signed < 0 ? '#EF456E' : '#919191';
              const amountText = formatAmountForDisplay(signed, signed < 0).text;
              const bc = (tx.references || []).find(r => r.type === 'BC')?.value;
              const catLabel = tx.category?.label || '';
              return (
                <Box
                  key={tx.id}
                  onClick={() => toggleCommercial(tx.id)}
                  sx={{ ...ROW_STYLE, background: selected ? '#EBF5FF' : '#FFFFFF', '&:hover': { background: selected ? '#E0F0FF' : '#F9FAFB' } }}
                >
                  <Checkbox checked={selected} onChange={() => toggleCommercial(tx.id)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#38485C' }}>{tx.code}</div>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600, color: '#75808E' }}>{formatDate(tx.createdAt)}</div>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#38485C', wordBreak: 'break-word' }}>{catLabel}</div>
                    {bc && (
                      <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#027BC6' }}>BC N°{bc}</div>
                    )}
                  </Box>
                  <Box sx={{ width: '130px', flexShrink: 0 }}>
                    <span style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 700, color: amountColor }}>{amountText}</span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT PANEL — Verifications */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* Panel header */}
          <Box sx={{ flexShrink: 0, pb: '12px' }}>
            <p style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px', color: '#38485C', margin: '0 0 12px 0' }}>
              Liste des vérifications
            </p>
            <FilterInput
              type="search"
              value={verificationSearch}
              onChange={setVerificationSearch}
              onClear={() => setVerificationSearch('')}
              hasValue={!!verificationSearch}
            />
          </Box>

          {/* Column headers */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: '12px', pb: '6px', gap: '10px' }}>
            <Checkbox checked={isAllVerificationSelected} indeterminate={isSomeVerificationSelected} onChange={handleSelectAllVerification} />
            {[
              { key: 'createdAt', label: 'Vérification ID', sx: { flex: 1 } },
              { key: 'bc',        label: 'Bon de caisse', sx: { flex: 1 } },
              { key: 'amount',    label: 'Montant', sx: { width: '130px', flexShrink: 0 } },
            ].map(col => (
              <Box key={col.key} onClick={() => handleVerificationSort(col.key)} sx={{
                ...col.sx, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600,
                color: verificationSortBy === col.key ? '#027BC6' : '#919191',
                '&:hover': { color: '#027BC6' }, transition: 'color 0.2s',
              }}>
                <span>{col.label}</span>
                {verificationSortBy === col.key && (
                  <img src={chevronUpIcon} alt="" style={{ width: '12px', height: '12px', transform: verificationSortDir === 'DESC' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Scrollable rows */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loadingVerification ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={28} /></Box>
            ) : filteredVerificationTxs.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 4, fontFamily: 'Montserrat', fontSize: '14px', color: '#919191' }}>
                Aucune vérification disponible
              </Box>
            ) : filteredVerificationTxs.map(tx => {
              const selected = selectedVerificationIds.includes(tx.id);
              const bc = (tx.references || []).find(r => r.type === 'BC')?.value;
              const signedVerifAmount = (Number(tx.amount) || 0) * (Number(tx.caisseFactor) || 1);
              const verifAmountColor = signedVerifAmount < 0 ? '#EF456E' : '#0AD69F';
              const isCorrection = CORRECTION_LABELS.includes(tx.category?.label);
              const correctionBorderColor = signedVerifAmount < 0 ? '#EF456E' : '#0AD69F';
              return (
                <Box
                  key={tx.id}
                  onClick={() => toggleVerification(tx.id)}
                  sx={{
                    ...ROW_STYLE,
                    background: selected ? '#EBF5FF' : '#FFFFFF',
                    '&:hover': { background: selected ? '#E0F0FF' : '#F9FAFB' },
                    ...(isCorrection && { border: `1.5px dashed ${correctionBorderColor}` }),
                  }}
                >
                  <Checkbox checked={selected} onChange={() => toggleVerification(tx.id)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#38485C' }}>{tx.code}</div>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600, color: '#75808E' }}>{formatDate(tx.createdAt)}</div>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {bc ? (
                      <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: '#38485C' }}>BC N°{bc}</div>
                    ) : (
                      <div style={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#919191', fontStyle: 'italic' }}>—</div>
                    )}
                  </Box>
                  <Box sx={{ width: '130px', flexShrink: 0 }}>
                    <span style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 700, color: verifAmountColor }}>
                      {formatAmountForDisplay(signedVerifAmount, signedVerifAmount < 0).text}
                    </span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* SIDEBAR */}
        <Box sx={{
          width: SIDEBAR_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column',
          background: '#FFFFFF', borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          alignSelf: 'flex-start',
          maxHeight: '100%',
        }}>

          {/* Header block — fixed (not scrollable), flush with rounded corners */}
          <Box sx={{ background: '#E9F5FD', px: '12px', pt: '12px', pb: '12px', flexShrink: 0 }}>
            {/* Caisse + Versement ID */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '10px' }}>
              <Box>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Caisse</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: '#000000', opacity: 0.88, wordBreak: 'break-word', maxWidth: '160px' }}>{caisseName}</div>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Versement ID</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#027BC6' }}>{versementCode}</div>
              </Box>
            </Box>

            {/* Date + Solde lors du versement */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Date</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#38485C' }}>{versementDate}</div>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Solde lors du versement</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#027BC6' }}>
                  {formatAmountForDisplay(soldeLorsVersement, false).text}
                </div>
              </Box>
            </Box>
          </Box>

          {/* Solid separator after header — full width naturally (no padding on outer container) */}
          <Box sx={{ height: '1px', background: '#E3E3E3', flexShrink: 0 }} />

          {/* Scrollable content */}
          <Box sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '12px', gap: '4px' }}>

            {/* Metrics */}
            <Box sx={{ px: '4px' }}>
              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Versement sélectionné</span>
                <span style={{ ...SIDEBAR_VALUE, color: '#027BC6' }}>{formatAmountForDisplay(versementAmount, false).text}</span>
              </Box>

              <Box sx={{ borderTop: '1px dashed #E3E3E3', mx: '4px' }} />

              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Transactions ({selectedCommercialIds.length})</span>
                <span style={SIDEBAR_VALUE}>{hasCommercialSelection ? formatSigned(transactionsNet) : '—'}</span>
              </Box>
              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Écart versement/transactions</span>
                <span style={{ ...SIDEBAR_VALUE, color: ecartColor(ecartVT) }}>
                  {ecartVT !== null ? formatSigned(ecartVT) : '—'}
                </span>
              </Box>

              {/* Full-width solid separator inside metrics (mx cancels 12px outer + 4px inner) */}
              <Box sx={{ height: '1px', background: '#E3E3E3', mx: '-16px' }} />

              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Ventes ({selectedSalesTxs.length})</span>
                <span style={SIDEBAR_VALUE}>{hasCommercialSelection ? formatAmountForDisplay(transactionsSales, false).text : '—'}</span>
              </Box>

              <Box sx={{ borderTop: '1px dashed #E3E3E3', mx: '4px' }} />

              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Vérifications ({selectedVerificationIds.length})</span>
                <span style={SIDEBAR_VALUE}>{hasVerificationSelection ? formatAmountForDisplay(verificationNet, false).text : '—'}</span>
              </Box>
              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Écart transactions/vérification</span>
                <span style={{ ...SIDEBAR_VALUE, color: ecartColor(ecartTV) }}>
                  {ecartTV !== null ? formatSigned(ecartTV) : '—'}
                </span>
              </Box>
            </Box>

            {/* Full-width solid separator between écart and montant ajustement */}
            <Box sx={{ height: '1px', background: '#E3E3E3', mx: '-12px' }} />

            {/* Montant Ajustement */}
            <TextField
              type="text"
              inputMode="decimal"
              value={adjustmentAmount}
              onChange={e => setAdjustmentAmount(e.target.value)}
              placeholder="Montant ajustement"
              fullWidth
              sx={{
                mt: '8px',
                mb: '4px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '12px',
                  border: '1px solid #E3E3E3',
                  background: '#FFFFFF',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#38485C',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  '& input': { padding: '0 !important' },
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputBase-input::placeholder': { color: '#A3A3A3', opacity: 1 },
              }}
            />

            {/* Direction toggle — segmented control */}
            <Box sx={{
              display: 'flex', background: '#E3E3E33C', border: '1px solid #E3E3E3',
              borderRadius: '12px', height: '40px', flexShrink: 0, padding: '3px', gap: '3px', mb: '4px',
            }}>
              <Box
                onClick={() => setAdjustmentDirection(d => d === 'ADD' ? null : 'ADD')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '9px', userSelect: 'none', transition: 'all 0.15s',
                  background: adjustmentDirection === 'ADD' ? '#FFFFFF' : 'transparent',
                  border: adjustmentDirection === 'ADD' ? '1px solid #027BC6' : 'none',
                  color: adjustmentDirection === 'ADD' ? '#027BC6' : '#75808E',
                }}
              >
                + Ajouter
              </Box>
              <Box
                onClick={() => setAdjustmentDirection(d => d === 'DEDUCT' ? null : 'DEDUCT')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '9px', userSelect: 'none', transition: 'all 0.15s',
                  background: adjustmentDirection === 'DEDUCT' ? '#FFFFFF' : 'transparent',
                  border: adjustmentDirection === 'DEDUCT' ? '1px solid #EF456E' : 'none',
                  color: adjustmentDirection === 'DEDUCT' ? '#EF456E' : '#75808E',
                }}
              >
                - Déduire
              </Box>
            </Box>

            {/* Note — fixed height, scrolls internally */}
            <Box sx={{ borderRadius: '12px', border: '1px solid #E3E3E3', background: '#FFFFFF', padding: '12px' }}>
              <textarea
                value={adjustmentNote}
                onChange={e => setAdjustmentNote(e.target.value)}
                placeholder="Note"
                style={{
                  width: '100%',
                  height: '54px',
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#38485C',
                  backgroundColor: 'transparent',
                  resize: 'none',
                  overflowY: 'auto',
                  boxSizing: 'border-box',
                  display: 'block',
                }}
              />
            </Box>
            {/* Confirmer */}
            <ActionButton
              variant="contained"
              onClick={handleConfirmClick}
              sx={{
                mt: '8px',
                width: '100%', height: '44px', borderRadius: '12px',
                background: '#027BC6', fontFamily: 'Montserrat', fontWeight: 600, fontSize: '15px',
                textTransform: 'none', boxShadow: 'none',
                '&:hover': { background: '#025a9c', boxShadow: 'none' },
              }}
            >
              Confirmer
            </ActionButton>
            <Box
              component="button"
              onClick={() => history.goBack()}
              sx={{
                mt: '4px', width: '100%', background: 'none', border: 'none',
                cursor: 'pointer', fontFamily: 'Montserrat', fontWeight: 600,
                fontSize: '13px', color: '#027BC6', padding: '6px 0',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                opacity: 1, transition: 'opacity 0.15s',
                '&:hover': { opacity: 0.65 },
              }}
            >
              <img src={arrowBackIcon} alt="" style={{ width: '13px', height: '13px', filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1234%) hue-rotate(194deg) brightness(95%) contrast(101%)' }} />
              Précédent
            </Box>
          </Box>
        </Box>
      </Box>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        type="rapprochement"
        loading={submitLoading}
      />
    </Box>
  );
};

export default NouveauRapprochementStep2;
